import { OR, PACU, POST_OP, PRE_OP, WAITING_ROOM } from '../../../entities/room/enums';
import { sortByIdDesc } from '../../../entities/common/transducers';
import { getLogEntries, isPatientReady, isReadyForPickupPACU, isReadyForPickupPOSTOP } from '../tablet/utils';

/**
 * Pure helpers for the Waiting Room "timeline" layout.
 *
 * Kept free of React/DOM so the grouping rules can be unit tested, and kept separate from
 * `WaitingRoomMonitor.jsx` so the trial view never changes the behaviour of the original screen.
 */

export type PhaseType = typeof WAITING_ROOM | typeof PRE_OP | typeof OR | typeof PACU | typeof POST_OP;

export interface PhaseDefinition {
  type: PhaseType;
  /** English label shown in the timeline header. */
  label: string;
  /** Arabic label shown underneath for Middle East deployments. */
  labelArabic: string;
}

/** Ordered journey the timeline follows, left to right. */
export const TIMELINE_PHASES: PhaseDefinition[] = [
  { type: WAITING_ROOM, label: 'Waiting Room', labelArabic: 'غرفة الانتظار' },
  { type: PRE_OP, label: 'Pre-Op', labelArabic: 'التجهيز للعملية' },
  { type: OR, label: 'Surgery', labelArabic: 'في العمليات' },
  { type: PACU, label: 'Recovery', labelArabic: 'في الإفاقة' },
  { type: POST_OP, label: 'Post-Op', labelArabic: 'قسم الانعاش' },
];

export const PHASE_TYPES: PhaseType[] = TIMELINE_PHASES.map(phase => phase.type);

/** Returns the phases that should be rendered as columns for this hospital. */
export const getVisiblePhases = (hasPostop: boolean): PhaseDefinition[] =>
  hasPostop ? TIMELINE_PHASES : TIMELINE_PHASES.filter(phase => phase.type !== POST_OP);

export interface TimelinePatient {
  id: number | string;
  name?: string | null;
  icon?: string | null;
  color?: string | null;
  changedRoomAt?: string | null;
  room?: { id?: number | string; name?: string | null; type?: string | null } | null;
  log?: unknown;
  // Derived flags (mirrors the original waiting room screen)
  isPACUReady: boolean;
  isPOSTOPReady: boolean;
  isFamilyNotifiedPACU: boolean;
  isFamilyNotifiedPOSTOP: boolean;
}

/**
 * Adds the same derived "ready" / "family notified" flags the original waiting room uses,
 * so highlight borders, pickup icons and the ready sound behave identically.
 */
export const withDerivedFlags = <T extends { id: number | string; log?: unknown }>(patient: T): T & TimelinePatient => {
  const entries = getLogEntries(patient);
  return {
    ...(patient as any),
    isPACUReady: isPatientReady(entries)(PACU),
    isPOSTOPReady: isPatientReady(entries)(POST_OP),
    isFamilyNotifiedPACU: isReadyForPickupPACU(entries),
    isFamilyNotifiedPOSTOP: isReadyForPickupPOSTOP(entries),
  };
};

export type PatientsByPhase = Record<PhaseType, TimelinePatient[]>;

const emptyGroups = (): PatientsByPhase => ({
  [WAITING_ROOM]: [],
  [PRE_OP]: [],
  [OR]: [],
  [PACU]: [],
  [POST_OP]: [],
});

/**
 * Groups patients under the phase column they belong to.
 *
 * - Patients with no room, an `Unknown` room, or a non-journey room type (utility, staff, …)
 *   are dropped, since they have no phase on the timeline.
 * - When the Post-Op column is hidden (`hasPostop` off) any Post-Op patient is folded into the
 *   Recovery column rather than disappearing from the board.
 * - Each column is ordered by when the patient entered their current room, most recent first,
 *   so someone who just moved shows up at the top of the new column (and on the first carousel
 *   page). Patients without a room-entry time go last; ties fall back to `sortByIdDesc`.
 */
export const groupPatientsByPhase = (patients: TimelinePatient[] = [], hasPostop: boolean): PatientsByPhase => {
  const groups = emptyGroups();

  for (const patient of patients) {
    const type = patient?.room?.type as PhaseType | undefined;
    if (!type || !PHASE_TYPES.includes(type)) continue;

    const target: PhaseType = type === POST_OP && !hasPostop ? PACU : type;
    groups[target].push(patient);
  }

  (Object.keys(groups) as PhaseType[]).forEach(type => {
    groups[type] = groups[type].sort(byMostRecentRoomEntry);
  });

  return groups;
};

const roomEntryTime = (patient: TimelinePatient): number | null => {
  if (!patient?.changedRoomAt) return null;
  const time = new Date(patient.changedRoomAt).getTime();
  return Number.isFinite(time) ? time : null;
};

export const byMostRecentRoomEntry = (left: TimelinePatient, right: TimelinePatient): number => {
  const leftTime = roomEntryTime(left);
  const rightTime = roomEntryTime(right);
  if (leftTime !== rightTime) {
    if (leftTime === null) return 1;
    if (rightTime === null) return -1;
    return rightTime - leftTime;
  }
  return sortByIdDesc(left, right);
};

/** Count used to trigger the "ready to see family" sound; identical to the original screen. */
export const countReadyToSeeFamily = (patients: TimelinePatient[] = []): number =>
  patients.filter(p => !!(p.isPACUReady || p.isPOSTOPReady)).length;

export const isHighlightedReady = (patient: TimelinePatient): boolean => {
  const roomType = patient?.room?.type;
  return (patient.isPACUReady && roomType === PACU) || (patient.isPOSTOPReady && roomType === POST_OP);
};

export const isReadyForPickup = (patient: TimelinePatient): boolean => {
  const roomType = patient?.room?.type;
  return (
    (patient.isFamilyNotifiedPACU && roomType === PACU) || (patient.isFamilyNotifiedPOSTOP && roomType === POST_OP)
  );
};

/**
 * Key that changes whenever the patient's displayed location changes, used to replay the
 * highlight animation (same rule as the original: OR patients key on the room *type* so moving
 * between ORs doesn't re-flash, everyone else keys on the room name).
 */
export const getHighlightKey = (patient: TimelinePatient): string => {
  const roomType = patient?.room?.type;
  return roomType === OR ? OR : patient?.room?.name || roomType || 'Unknown';
};

export type Density = 'large' | 'normal' | 'compact';

/**
 * Picks a type-size tier from the busiest column. Fewer patients → bigger cards (like the original
 * screen scaling from 5vw to 3vw); a very busy column drops to a compact tier so each carousel
 * page holds more names and the full rotation stays short.
 */
export const getDensity = (maxPatientsInAColumn: number): Density => {
  if (maxPatientsInAColumn <= 4) return 'large';
  if (maxPatientsInAColumn <= 9) return 'normal';
  return 'compact';
};

const DENSITY_FACTOR: Record<Density, number> = {
  large: 0.1,
  normal: 0.085,
  compact: 0.065,
};

/**
 * Card font size in `vw`, scaled by how wide each column is. With 5 columns on a 1080p TV this
 * is ~38px / 33px / 25px for the three tiers, sized so icon + name fit on one row inside a
 * ~355px column. (The original 3vw cards had the whole screen width to work with.)
 */
export const getCardFontSizeVw = (columns: number, density: Density): number => {
  const safeColumns = Math.max(1, columns || 1);
  const columnWidthVw = 100 / safeColumns;
  return Math.round(columnWidthVw * DENSITY_FACTOR[density] * 100) / 100;
};

/** Splits a list into pages of `size`. Always returns at least one (possibly empty) page. */
export const paginate = <T>(items: T[] = [], size: number): T[][] => {
  const safeSize = Math.max(1, Math.floor(size) || 1);
  const count = Math.max(1, Math.ceil(items.length / safeSize));
  return [...Array(count)].map((_, idx) => items.slice(idx * safeSize, idx * safeSize + safeSize));
};
